import { useMutation } from '@apollo/client';
import { Bee, BeeTag, UpdateBee, UpdateBeeMutation, UpdateBeeMutationVariables } from '@serodigital/ies-api-client';
import { Card, Text, Toggle, Tooltip } from '@serodigital/ui-components';
import * as React from 'react';

type IDataProcessingProps = {
  bee: Bee;
  allowedToEditTags: boolean;
};

type IDataProcessingStatusProps = {
  title: string;
  description: string;
  children: React.ReactNode;
};

type TrustmarkStatusProps = {
  bee: Bee;
  allowedToEditTags: boolean;
};

function TrustmarkStatus({ bee, allowedToEditTags }: TrustmarkStatusProps) {
  const [updateBee, { loading, data }] = useMutation<UpdateBeeMutation, UpdateBeeMutationVariables>(UpdateBee);
  const tags = data?.updateBee?.bee?.tags || bee?.tags;
  const trustmarkChecked = tags?.includes(BeeTag.Trustmark);

  return (
    <div className="flex flex-row">
      <div>
        <div className="pb-12">
          <Text>Sending Data (OFF/ON)</Text>
        </div>
        <div className="flex items-center space-x-12">
          <Toggle
            checked={trustmarkChecked}
            disabled={loading || !allowedToEditTags}
            onChange={async (checked) => {
              await updateBee({
                variables: {
                  bee: {
                    deviceId: bee?.deviceId,
                    tags: checked ? tags.concat(BeeTag.Trustmark) : tags.filter((tag) => tag !== BeeTag.Trustmark),
                  },
                },
              });
            }}
            data-qa="trustmark-data-processing-toggle"
          />
          {!allowedToEditTags && (
            <Tooltip
              data-qa={'trustmark-data-processing'}
              placement={'bottom'}
              TooltipComponent={() => (
                <Text data-qa="no-permission-message">
                  You do not have the correct account permissions <br />
                  to control a heat source.
                </Text>
              )}
              hasArrow={true}
            >
              <img
                src={require('../../../../assets/icons/information.svg')}
                alt="Information"
                width={22}
                data-qa="information-icon"
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

function DataProcessingStatus({ title, description, children }: IDataProcessingStatusProps) {
  return (
    <div className="flex justify-between items-center sero-p-4" data-qa={`${title}-processing-status`}>
      <div className="flex flex-col">
        <Text className="sero-text-brand-primary sero-text-large" weight="bold">
          {title}
        </Text>
        <Text className='sero-text-grey-primary-text sero-text'>{description}</Text>
      </div>
      {children}
    </div>
  );
}

export function DataProcessing({ bee, allowedToEditTags }: IDataProcessingProps) {
  return (
    <Card data-qa={'data-processing-card'}>
      <div className="flex flex-col sero-p-4">
        <Text className='sero-text-grey-primary-text sero-text-large' weight="bold">
          Data processing
        </Text>

        <Text className='sero-text-grey-primary-text sero-text'>Settings around data processing for a home.</Text>
      </div>
      <div className='h-1 w-full sero-bg-grey-neutral my-16' />
      <DataProcessingStatus
        title="Trustmark"
        description="Data from the home is being sent to trustmark and is approved to do so."
      >
        <TrustmarkStatus bee={bee} allowedToEditTags={allowedToEditTags} />
      </DataProcessingStatus>
    </Card>
  );
}
